import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import Reveal from '../../../components/shared/Reveal';
import { EditableText } from '../../components';

/**
 * registration-form — admissions multi-step form. Data-driven from seed.
 * Field shape supports `options` (inline) or `options_ref` referencing
 * `data.sessions` / `data.programs`, and `show_when: { field, equals }`.
 */
function RegistrationForm({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const updateSuccess = (patch) => update({ success: { ...(data.success || {}), ...patch } });
  const updateStep = (i, patch) => {
    const next = (data.steps || []).map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    update({ steps: next });
  };
  const steps = useMemo(() => data.steps || [], [data.steps]);
  const sessions = data.sessions || [];
  const programs = data.programs || [];

  const initialFormData = useMemo(() =>
    steps.reduce((acc, step) => {
      (step.fields || []).forEach((f) => { acc[f.name] = ''; });
      return acc;
    }, {}),
  [steps]);

  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!steps.length) return null;
  const currentStep = steps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / steps.length) * 100);

  const resolveOptions = (field) => {
    if (field.options_ref === 'sessions') return sessions;
    if (field.options_ref === 'programs') return programs;
    return field.options || [];
  };

  const shouldShow = (field) => {
    const sw = field.show_when;
    if (!sw) return true;
    return formData[sw.field] === sw.equals;
  };

  const handleChange = (name) => (e) => {
    const value = e.target.value;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: '' }));
  };

  const validateStep = () => {
    const stepErrors = {};
    (currentStep.fields || []).forEach((field) => {
      if (shouldShow(field) && field.required && !formData[field.name]) {
        stepErrors[field.name] = 'Required';
      }
    });
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    if (stepIndex < steps.length - 1) setStepIndex((p) => p + 1);
    else setIsSubmitted(true);
  };
  const handleBack = () => setStepIndex((p) => Math.max(p - 1, 0));
  const resetForm = () => { setFormData(initialFormData); setErrors({}); setIsSubmitted(false); setStepIndex(0); };

  const renderField = (field) => {
    if (!shouldShow(field)) return null;
    const base = 'input-base';
    if (field.type === 'select') {
      const opts = resolveOptions(field);
      return (
        <label key={field.name} className="block">
          <span className="text-xs text-ink/70 font-medium">{field.label}{field.required && ' *'}</span>
          <select value={formData[field.name]} onChange={handleChange(field.name)} required={field.required} className={`${base} mt-1.5`}>
            <option value="" disabled>Select</option>
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {errors[field.name] && <span className="text-xs text-red-600 mt-1 block">{errors[field.name]}</span>}
        </label>
      );
    }
    if (field.type === 'textarea') {
      return (
        <label key={field.name} className="block sm:col-span-2">
          <span className="text-xs text-ink/70 font-medium">{field.label}{field.required && ' *'}</span>
          <textarea value={formData[field.name]} onChange={handleChange(field.name)} placeholder={field.placeholder} rows={4} className={`${base} mt-1.5`} />
          {errors[field.name] && <span className="text-xs text-red-600 mt-1 block">{errors[field.name]}</span>}
        </label>
      );
    }
    return (
      <label key={field.name} className="block">
        <span className="text-xs text-ink/70 font-medium">{field.label}{field.required && ' *'}</span>
        <input type={field.type} value={formData[field.name]} onChange={handleChange(field.name)} placeholder={field.placeholder} required={field.required} className={`${base} mt-1.5`} />
        {errors[field.name] && <span className="text-xs text-red-600 mt-1 block">{errors[field.name]}</span>}
      </label>
    );
  };

  const success = data.success || {};

  return (
    <section className="py-24 bg-white">
      <div className="container-page">
        <Reveal>
          <div className="card-surface p-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <EditableText value={data.title || 'Registration Form'} onChange={(t) => update({ title: t })} as="h3" className="font-display text-2xl" />
              <span className="text-xs uppercase tracking-wider text-ink/60">Step {stepIndex + 1} of {steps.length}</span>
            </div>
            <div className="mt-4 h-1.5 w-full bg-olive-light rounded-full overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} className="h-full bg-gradient-to-r from-olive to-accent rounded-full" />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {steps.map((s, i) => (
                <button key={s.id || i} type="button" onClick={() => i < stepIndex && setStepIndex(i)} className={`text-xs px-3 py-1.5 rounded-full transition ${i === stepIndex ? 'bg-olive text-white' : i < stepIndex ? 'bg-olive-light text-olive-deep' : 'bg-gray-100 text-ink/40'}`}>
                  {i < stepIndex && <Check size={12} className="inline mr-1" />}
                  <EditableText value={s.title} onChange={(t) => updateStep(i, { title: t })} as="span" />
                </button>
              ))}
            </div>

            {isSubmitted ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mt-10 text-center py-12">
                <div className="inline-flex h-20 w-20 rounded-full bg-olive text-white items-center justify-center"><Check size={40} /></div>
                <EditableText value={success.heading || 'Thank you!'} onChange={(t) => updateSuccess({ heading: t })} as="h4" className="font-display text-3xl mt-6" />
                <EditableText value={success.body} onChange={(t) => updateSuccess({ body: t })} as="p" multiline className="mt-3 text-ink/70 max-w-md mx-auto" />
                <button type="button" onClick={resetForm} className="btn-primary mt-8">
                  <EditableText value={success.reset_label || 'Submit another response'} onChange={(t) => updateSuccess({ reset_label: t })} as="span" />
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleNext} className="mt-8">
                <AnimatePresence mode="wait">
                  <motion.div key={currentStep.id || stepIndex} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                    <EditableText value={currentStep.title} onChange={(t) => updateStep(stepIndex, { title: t })} as="h4" className="font-display text-xl text-olive-deep" />
                    <EditableText value={currentStep.description} onChange={(t) => updateStep(stepIndex, { description: t })} as="p" multiline className="text-sm text-ink/60 mt-1" />
                    <div className="mt-6 grid sm:grid-cols-2 gap-4">{(currentStep.fields || []).map(renderField)}</div>
                  </motion.div>
                </AnimatePresence>
                <div className="mt-8 flex items-center justify-between gap-3">
                  <button type="button" onClick={handleBack} disabled={stepIndex === 0} className="btn-ghost disabled:opacity-40">
                    <ArrowLeft size={16} />
                    <EditableText value={data.back_label || 'Back'} onChange={(t) => update({ back_label: t })} as="span" />
                  </button>
                  <button type="submit" className="btn-primary">
                    {stepIndex === steps.length - 1 ? (
                      <EditableText value={data.submit_label || 'Submit'} onChange={(t) => update({ submit_label: t })} as="span" />
                    ) : (
                      <EditableText value={data.continue_label || 'Continue'} onChange={(t) => update({ continue_label: t })} as="span" />
                    )}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default RegistrationForm;
